import { Combobox, SelectOption } from '@payaca/ambrosia';
import * as customerActions from '@payaca/store/customer/customerActions';
import { useCustomer } from '@payaca/store/hooks/appState';
import { Customer } from '@payaca/types/customerTypes';
import { ListedCustomer } from '@payaca/types/listedCustomerTypes';
import {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from 'react';
import { useDispatch } from 'react-redux';

interface Props {
  customerId?: Customer['id'];
  onChange: (
    customerId?: Customer['id'],
    metadata?: { customer?: Customer }
  ) => void;
  disabled?: boolean;
  showCardIfDisabled?: boolean;
  /**
   * Called as a selection starts and finishes resolving. `onChange` only fires
   * once the chosen Customer has been fetched, so until then a consumer still
   * holds the previous selection — gate anything that acts on the value on
   * this, or it will act on a stale one.
   */
  onResolvingChange?: (isResolving: boolean) => void;
}

export const CustomerSelect: FC<Props> = ({
  customerId,
  onChange,
  disabled,
  showCardIfDisabled,
  onResolvingChange,
}: Props): JSX.Element => {
  const dispatch = useDispatch();
  const [query, setQuery] = useState<string>();
  const [requiresGetListedCustomersPage, setRequiresGetListedCustomersPage] =
    useState(false);
  const changeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isChanging, setIsChanging] = useState(false);

  const retrievedCustomer = useCustomer(customerId);
  const [customer, setCustomer] = useState<Customer>();

  const [listedCustomers, setListedCustomers] = useState<ListedCustomer[]>([]);

  useEffect(() => {
    if (changeTimeout?.current) {
      clearTimeout(changeTimeout.current);
    }
    changeTimeout.current = setTimeout(() => {
      setRequiresGetListedCustomersPage(true);
    }, 500);
  }, [query]);

  const requestGetListedCustomersPage = useCallback(() => {
    dispatch(
      customerActions.requestGetListedCustomersPage(
        {
          pageSize: 20,
          pageNumber: 1,
          searchTerm: query,
        },
        (listedCustomers) => {
          setListedCustomers(listedCustomers.items);
        }
      )
    );
    setRequiresGetListedCustomersPage(false);
  }, [query, dispatch]);

  useEffect(() => {
    if (!customerId) {
      setRequiresGetListedCustomersPage(true);
    } else if (retrievedCustomer) {
      setCustomer(retrievedCustomer);
    } else {
      dispatch(
        customerActions.requestGetCustomer(customerId, (customer) => {
          setCustomer(customer);
        })
      );
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has missing dependencies: 'dispatch', and 'retrievedCustomer'
  }, [customerId]);

  useEffect(() => {
    if (requiresGetListedCustomersPage) {
      requestGetListedCustomersPage();
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'requestGetListedCustomersPage'
  }, [requiresGetListedCustomersPage]);

  const options = useMemo(() => {
    const o: SelectOption<number>[] = listedCustomers.map((customer) => {
      return {
        label: customer.customerName,
        value: customer.customerId,
      };
    });

    if (customer && !o.find((x) => x.value === customer.id)) {
      o.push({
        value: customer.id,
        label: customer.name,
      });
    }

    return o;
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: This dependency is specified more than once in the dependency array.
  }, [listedCustomers, customer, customer]);

  const handleOnChange = useCallback(
    (value: any) => {
      const customerId = value as Customer['id'] | undefined;

      setCustomer(undefined);
      setCustomer(undefined);

      if (!customerId) {
        onChange();
        onResolvingChange?.(false);
      } else {
        setIsChanging(true);
        onResolvingChange?.(true);
        dispatch(
          customerActions.requestGetCustomer(customerId, (customer) => {
            setCustomer(customer);

            onChange(customerId, { customer });
            setIsChanging(false);
            onResolvingChange?.(false);
          })
        );
      }
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'dispatch'
    [onChange, onResolvingChange]
  );

  if (disabled && customer && showCardIfDisabled)
    return (
      <div className="border py-3 px-4 border-gray-200 rounded-md shadow-sm">
        {customer.name}
      </div>
    );

  return (
    <Combobox
      disabled={disabled}
      onChange={handleOnChange}
      options={options}
      value={isChanging ? undefined : customerId}
      placeholder={isChanging || customerId ? '...' : undefined}
      query={query}
      setQuery={setQuery}
      filterFunction={() => true}
    />
  );
};
